import { useEffect, useState, type ReactNode } from "react";

// The loading frame for a page body. The route keeps its real header
// above this (title, subtitle, breadcrumb) so the frame lands at once
// and only the content skeletonizes (handoff §11). The wrapper is
// `aria-busy` and carries one polite announcement; the Skel blocks
// inside are aria-hidden. Both go away with the loading branch, so
// aria-busy never lingers after the data lands. Never put aria-busy on
// <main> — that's the route's landmark, not the loading content.
//
// `visible` follows the shared phase machine (useQueryView): during the
// first SKELETON_DELAY_MS the frame is mounted but `visibility: hidden`
// ([data-skel-visible="false"] in dashboard.css), reserving the body's
// height so a fast load neither flashes nor shifts.
//
// The status element is mounted empty and gets its text only once the
// skeleton is visible: a fast load stays silent, and screen readers that
// ignore text already present when a live region is inserted still hear
// the change. It is a SIBLING of the busy frame, not inside it: AT may
// hold back updates inside an aria-busy subtree (and this one is removed
// rather than un-busied), and the hidden pending frame would hide it.
// `announce={false}` (sections) drops it entirely — a page gets one
// announcement, not one per panel.
type Props = {
  /** The full status sentence, e.g. «Cargando tus tiendas…».
   *  (QueryBoundary takes a noun phrase and builds this sentence.) */
  label: string;
  children: ReactNode;
  visible?: boolean;
  announce?: boolean;
  kind?: "page" | "section";
};

export function PageSkeleton({
  label,
  children,
  visible = true,
  announce = true,
  kind = "page",
}: Props) {
  const [announced, setAnnounced] = useState(false);
  useEffect(() => {
    if (visible) setAnnounced(true);
  }, [visible]);

  return (
    <>
      {announce && (
        <span className="sr-only" role="status">
          {announced ? label : ""}
        </span>
      )}
      <div aria-busy="true" data-skeleton={kind} data-skel-visible={String(visible)}>
        {children}
      </div>
    </>
  );
}
